// src/api.js
import axios from 'axios';

// Configure axios to send credentials (cookies) with requests
axios.defaults.withCredentials = true;

// Use REACT_APP_API_BASE_URL if set, otherwise default to localhost for development
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.spinnercs.com';

// --- Admin User Management ---
export const getAllUsers = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/Users`);
        return response.data; // Array of User objects
    } catch (error) {
        console.error("Error fetching all users:", error.response?.data || error.message);
        throw error;
    }
};

export const getUserById = async (userId) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/Users/${userId}`);
        return response.data; // Single User object
    } catch (error) {
        console.error(`Error fetching user ${userId}:`, error.response?.data || error.message);
        throw error;
    }
};

export const updateUser = async (userId, userData) => {
    try {
        // Ensure userData matches the backend's expected User schema for PUT
        const response = await axios.put(`${API_BASE_URL}/api/Users/${userId}`, userData);
        return response.data; // Or handle 204 No Content
    } catch (error) {
        console.error(`Error updating user ${userId}:`, error.response?.data || error.message);
        throw error;
    }
};

export const deleteUser = async (userId) => {
    try {
        await axios.delete(`${API_BASE_URL}/api/Users/${userId}`);
        // Handle 204 No Content
    } catch (error) {
        console.error(`Error deleting user ${userId}:`, error.response?.data || error.message);
        throw error;
    }
};

// --- Admin Item Management ---
export const getAllItems = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/Items`);
        return response.data; // Array of Item objects
    } catch (error) {
        console.error("Error fetching all items:", error.response?.data || error.message);
        throw error;
    }
};

export const addItem = async (itemData) => {
    try {
        // Ensure itemData matches the backend's expected Item schema for POST
        const response = await axios.post(`${API_BASE_URL}/api/Items`, itemData);
        return response.data; // The created Item object
    } catch (error) {
        console.error("Error adding item:", error.response?.data || error.message);
        throw error;
    }
};

export const updateItem = async (assetId, itemData) => {
    try {
        // Ensure itemData matches the backend's expected Item schema for PUT
        const response = await axios.put(`${API_BASE_URL}/api/Items/${assetId}`, itemData);
        return response.data; // Or handle 204 No Content
    } catch (error) {
        console.error(`Error updating item ${assetId}:`, error.response?.data || error.message);
        throw error;
    }
};

export const deleteItem = async (assetId) => {
    try {
        await axios.delete(`${API_BASE_URL}/api/Items/${assetId}`);
        // Handle 204 No Content
    } catch (error) {
        console.error(`Error deleting item ${assetId}:`, error.response?.data || error.message);
        throw error;
    }
};

export const refreshItems = async () => {
    try {
        await axios.post(`${API_BASE_URL}/api/Items/refresh`);
        // Handle 200 OK
    } catch (error) {
        console.error("Error refreshing items:", error.response?.data || error.message);
        throw error;
    }
};


// --- Admin Featured Item Management ---
// GET /api/Items/featured is already defined

export const addFeaturedItem = async (featuredItemData) => {
    try {
        // Ensure featuredItemData matches the backend's expected FeaturedItem schema for POST
        const response = await axios.post(`${API_BASE_URL}/api/Items/featured`, featuredItemData);
        return response.data; // The created FeaturedItem object
    } catch (error) {
        console.error("Error adding featured item:", error.response?.data || error.message);
        throw error;
    }
};

export const updateFeaturedItem = async (assetId, featuredItemData) => {
    try {
        // Ensure featuredItemData matches the backend's expected FeaturedItem schema for PUT
        const response = await axios.put(`${API_BASE_URL}/api/Items/featured/${assetId}`, featuredItemData);
        return response.data; // Or handle 204 No Content
    } catch (error) {
        console.error(`Error updating featured item ${assetId}:`, error.response?.data || error.message);
        throw error;
    }
};

export const deleteFeaturedItem = async (assetId) => {
    try {
        await axios.delete(`${API_BASE_URL}/api/Items/featured/${assetId}`);
        // Handle 204 No Content
    } catch (error) {
        console.error(`Error deleting featured item ${assetId}:`, error.response?.data || error.message);
        throw error;
    }
};


export const getAuthStatus = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/auth/status`);
        // Assuming the backend sends user data including spinCount on successful auth
        return response.data;
    } catch (error) {
        console.error("Error fetching auth status:", error);
        return { isAuthenticated: false };
    }
};

export const logoutUser = async () => {
    try {
        await axios.get(`${API_BASE_URL}/auth/logout`); // Consider changing backend to POST
    } catch (error) {
        console.error("Error logging out:", error);
        // Handle error appropriately
    }
};

// --- START: Message Management ---

/**
 * Retrieves all messages (sent and received) for the authenticated user.
 * Requires authentication.
 * @returns {Promise<Array<object>>} An array of Message objects.
 */
export const getMyMessages = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/messages/my-messages`);
        return response.data; // Array of Message objects
    } catch (error) {
        console.error("Error fetching user messages:", error.response?.data || error.message);
        throw error;
    }
};

/**
 * Sends a message from the authenticated user to another user.
 * Requires authentication. Regular users can only message admins. Admins can message anyone.
 * @param {object} messageData - The message data.
 * @param {number} messageData.toUserId - The ID of the recipient user.
 * @param {string} messageData.content - The message content (1-1000 chars).
 * @param {string} [messageData.title] - Optional message title (max 100 chars).
 * @param {number} [messageData.replyToMessageId] - Optional ID of the message this is a reply to.
 * @returns {Promise<object>} The created Message object.
 */
export const getOffers = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/offers`);
        return response.data;
    } catch (error) {
        console.error("Error fetching offers:", error);
        throw error; // Re-throw to be handled by the component
    }
};

export const performSpin = async () => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/spins`);
        return response.data; // The won item
    } catch (error) {
        console.error("Error performing spin:", error.response?.data || error.message);
        throw error; // Re-throw
    }
};

export const setTradeUrl = async (tradeUrl) => {
    try {
        // Backend expects a raw string in the body
        const response = await axios.put(`${API_BASE_URL}/api/users/me/tradeurl`, JSON.stringify(tradeUrl), {
            headers: { 'Content-Type': 'application/json' }
        });
        return response.data;
    } catch (error) {
        console.error("Error setting trade URL:", error.response?.data || error.message);
        throw error;
    }
};

export const sendMessage = async (messageData) => {
    try {
        // Ensure messageData matches the SendMessageDto structure
        const response = await axios.post(`${API_BASE_URL}/api/messages`, messageData);
        return response.data; // The created Message object
    } catch (error) {
        console.error("Error sending message:", error.response?.data || error.message);
        throw error;
    }
};
// --- END: Message Management ---

export const getUserSpinCount = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/users/me/spins`);
        return response.data; // Adjust based on your backend response structure
    } catch (error) {
        console.error("Error fetching spin count:", error.response?.data || error.message);
        throw error; // Re-throw for component handling
    }
};

export const getUserInventory = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/users/me/inventory`);
        return response.data; // Returns the array of inventory items
    } catch (error) {
        console.error("Error fetching user inventory:", error.response?.data || error.message);
        throw error; // Re-throw for component handling
    }
};

// --- New function to get featured items ---
/**
 * Fetches the featured items for the spin page carousel.
 * Assumes the backend returns an array of items like the example provided.
 */
export const getFeaturedItems = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/Items/featured`);
        return response.data; // Returns the array of featured items
    } catch (error) {
        console.error("Error fetching featured items:", error.response?.data || error.message);
        throw error; // Re-throw for component handling
    }
};
// --- End of new function ---
// --- START: Admin Bot Management ---

/**
 * Retrieves a list of all configured bot accounts.
 * Requires Admin role.
 */
export const getAllBots = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/bots`);
        return response.data; // Array of Bot objects
    } catch (error) {
        console.error("Error fetching all bots:", error.response?.data || error.message);
        throw error;
    }
};

/**
 * Retrieves a specific bot account by its database ID.
 * Requires Admin role.
 * @param {number} botId The unique database ID of the bot.
 */
export const getBotById = async (botId) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/bots/${botId}`);
        return response.data; // Single Bot object
    } catch (error) {
        console.error(`Error fetching bot ${botId}:`, error.response?.data || error.message);
        throw error;
    }
};

/**
 * Adds a new bot account configuration.
 * Requires Admin role.
 * @param {object} botData Object containing bot details (e.g., { steamId, description }).
 */
export const addBot = async (botData) => {
    try {
        // Ensure botData matches the backend's expected schema for POST
        const response = await axios.post(`${API_BASE_URL}/api/bots`, botData);
        return response.data; // The created Bot object
    } catch (error) {
        console.error("Error adding bot:", error.response?.data || error.message);
        throw error;
    }
};

/**
 * Updates an existing bot account configuration (e.g., description).
 * Requires Admin role.
 * @param {number} botId The unique database ID of the bot to update.
 * @param {object} botData Object containing updated bot details. The botId in the data should match the route param.
 */
export const updateBot = async (botId, botData) => {
    try {
        // Ensure botData matches the backend's expected schema for PUT
        const response = await axios.put(`${API_BASE_URL}/api/bots/${botId}`, botData);
        return response.data; // Or handle 204 No Content
    } catch (error) {
        console.error(`Error updating bot ${botId}:`, error.response?.data || error.message);
        throw error;
    }
};

/**
 * Removes a bot account configuration.
 * Requires Admin role.
 * @param {number} botId The unique database ID of the bot to delete.
 */
export const deleteBot = async (botId) => {
    try {
        await axios.delete(`${API_BASE_URL}/api/bots/${botId}`);
        // Handle 204 No Content
    } catch (error) {
        console.error(`Error deleting bot ${botId}:`, error.response?.data || error.message);
        throw error;
    }
};

// --- END: Admin Bot Management ---
/**
 * Fetches the current user's saved trade URL.
 * Assumes the backend returns the URL as a string in the response data,
 * or potentially null/empty string if not set.
 */
export const getUserTradeUrl = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/users/me/tradeurl`);
        // The backend might return the URL directly, or within a data object.
        // Adjust `response.data` if needed based on your backend structure.
        // If the backend returns an empty body or 204 No Content for no URL,
        // axios might resolve with `undefined` or an empty string depending on config.
        // We'll handle null/undefined/empty string in the component.
        return response.data;
    } catch (error) {
        // If the backend returns 404 Not Found when no URL is set,
        // we might want to treat that as "no URL" rather than an error.
        if (error.response && error.response.status === 404) {
            console.log("No trade URL found for user.");
            return null; // Or return an empty string ''
        }
        console.error("Error fetching trade URL:", error.response?.data || error.message);
        throw error; // Re-throw other errors
    }

};
/**
 * Updates only the spin count for a specific user.
 * Requires Admin role.
 * @param {string} userId The unique ID (SteamID) of the user.
 * @param {number} spins The new spin count.
 */
export const updateUserSpins = async (userId, spins) => {
    try {
        // Backend expects an object like { "spins": number } in the body
        const response = await axios.put(`${API_BASE_URL}/api/Users/${userId}/spins`, { spins });
        return response.data; // Or handle 204 No Content
    } catch (error) {
        console.error(`Error updating spins for user ${userId}:`, error.response?.data || error.message);
        throw error;
    }
};

/**
 * Updates only the flags for a specific item.
 * Requires Admin role.
 * @param {string} assetId The unique asset ID of the item.
 * @param {object} flagsData Object containing the flags, e.g., { excludedFromSpin: boolean, waitingForTrade: boolean }.
 */
export const updateItemFlags = async (assetId, flagsData) => {
    try {
        // Backend expects an object like { "excludedFromSpin": true, "waitingForTrade": true }
        const response = await axios.put(`${API_BASE_URL}/api/Items/${assetId}/flags`, flagsData);
        return response.data; // Or handle 204 No Content
    } catch (error) {
        console.error(`Error updating flags for item ${assetId}:`, error.response?.data || error.message);
        throw error;
    }
};

/**
 * Initiates a withdrawal request for the specified item asset IDs.
 * Requires authentication and a saved Trade URL.
 * @param {string[]} assetIds - An array of asset IDs to withdraw.
 * @returns {Promise<object>} - The response data from the backend, including success status, message, and details on success/failure.
 */
export const withdrawItems = async (assetIds) => {
    if (!assetIds || assetIds.length === 0) {
        // Prevent API call if no items are selected
        return Promise.reject(new Error("No items selected for withdrawal."));
    }
    try {
        const response = await axios.post(`${API_BASE_URL}/api/inventory/withdraw`, { assetIds });
        // The backend returns 200 OK even for partial success/full failure if the request is valid.
        // The component needs to check the response body.
        return response.data;
    } catch (error) {
        console.error("Error withdrawing items:", error.response?.data || error.message);
        // Re-throw the error so the component can access response details (like status code and data)
        throw error;
    }
};

/**
 * Fetches the inventory for a specific user by their User ID.
 * Requires Admin role.
 * @param {number} userId The unique database ID of the user.
 * @returns {Promise<Array<object>>} An array of inventory item objects.
 */
export const getUserInventoryById = async (userId) => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/Users/${userId}/inventory`);
        return response.data; // Returns the array of inventory items
    } catch (error) {
        console.error(`Error fetching inventory for user ${userId}:`, error.response?.data || error.message);
        // Re-throw for component handling, especially 404 for empty/no inventory
        throw error;
    }
};

/**
 * Sets a specific tag for an item.
 * Requires Admin role.
 * @param {string} assetId The unique asset ID of the item.
 * @param {string} itemTag The tag to assign ("", "Staging", "Featured").
 */
export const updateItemTag = async (assetId, itemTag) => {
    try {
        // Backend expects an object like { "itemTag": "string" }
        const response = await axios.put(`${API_BASE_URL}/api/Items/${assetId}/itemtag`, { itemTag });
        return response.data; // Or handle 204 No Content
    } catch (error) {
        console.error(`Error updating tag for item ${assetId}:`, error.response?.data || error.message);
        throw error;
    }
};

/**
 * Sets a specific type for a user.
 * Requires Admin role.
 * @param {number} userId The unique internal database ID of the user.
 * @param {string} userType The type to assign ("", "Staging", "Restricted", "Bot").
 */
export const updateUserType = async (userId, userType) => {
    try {
        // Backend expects an object like { "userType": "string" }
        const response = await axios.put(`${API_BASE_URL}/api/Users/${userId}/usertype`, { userType });
        return response.data; // Or handle 204 No Content
    } catch (error) {
        console.error(`Error updating type for user ${userId}:`, error.response?.data || error.message);
        throw error;
    }
};

// --- START: Referral System ---

/**
 * Fetches the current user's referral code.
 * Requires authentication.
 * @returns {Promise<object>} - Object containing the referral code, e.g., { code: "REF123" } or null if none exists.
 * @returns {Promise<string|null>} - The user's referral code as a string, or null if an error occurs (empty string "" if not set).
 */
export const getUserReferralCode = async () => {
    try {
        // Use the correct endpoint from documentation
        const response = await axios.get(`${API_BASE_URL}/api/Users/me/referralcode`);
        // Backend returns the code directly as a string, or "" if not set.
        return response.data;
    } catch (error) {
        if (error.response && (error.response.status === 404 || error.response.status === 204)) {
            return ""; // Treat "Not Found" or "No Content" as code not set (empty string)
        }
        console.error("Error fetching user referral code:", error.response?.data || error.message);
        throw error;
    }
};

/**
 * Creates a referral code for the current user.
 * Requires authentication.
 * @param {string} referralCode - The desired referral code.
 * @returns {Promise<void>} - Resolves on success (204 No Content).
 */
export const setUserReferralCode = async (referralCode) => {
    try {
        // Use PUT and the correct endpoint and payload structure
        await axios.put(`${API_BASE_URL}/api/Users/me/referralcode`, { referralCode });
        // PUT returns 204 No Content on success, no data to return.
    } catch (error) {
        // Keep detailed error logging
        console.error("Error creating referral code:", error.response?.data || error.message);
        throw error;
    }
};

/**
 * Fetches the number of spins earned by the current user through referrals.
 * Requires authentication.
 * @returns {Promise<number>} - The number of spins earned from referrals.
 */
export const getUserReferralSpins = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/Users/me/referralspins`);
        // Backend returns the count directly as an integer.
        return response.data;
    } catch (error) {
        console.error("Error fetching referral spins count:", error.response?.data || error.message);
        throw error; // Re-throw for component handling
    }
};

// --- END: Referral System ---

// --- START: Admin Item Purge ---

/**
 * [DANGEROUS] Purges ALL items from the Items and UserInventoryItems tables.
 * Requires Admin role.
 */
export const purgeAllItems = async () => {
    try {
        await axios.post(`${API_BASE_URL}/api/items/purge-all`);
        // Handle 204 No Content (success)
    } catch (error) {
        console.error("Error purging all items:", error.response?.data || error.message);
        throw error;
    }
};
// --- END: Admin Item Purge ---
/**
 * Purges specified inventory items for a user.
 * Removes items from UserInventoryItems if not withdrawn, and resets WaitingForTrade in Items table.
 * Requires Admin role.
 * @param {number} userId The unique identifier of the user.
 * @returns {Promise<object>} An object detailing the outcome of the purge operation.
 */
export const purgeUserInventoryItems = async (userId) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/inventory/user/${userId}/purge-items`);
        return response.data; // PurgeUserInventoryResponseDto
    } catch (error) {
        console.error(`Error purging inventory for user ${userId}:`, error.response?.data || error.message);
        throw error;
    }
};

/**
 * Submits feedback for a specific offer.
 * Requires authentication.
 * @param {object} feedbackData - The feedback data.
 * @param {number} feedbackData.offerId - The ID of the offer.
 * @param {string} [feedbackData.comment] - Optional user comment (max 400 chars).
 * @returns {Promise<object>} The created OfferFeedback object.
 */
export const submitOfferFeedback = async (feedbackData) => {
    try {
        const response = await axios.post(`${API_BASE_URL}/api/OfferFeedbacks`, feedbackData);
        return response.data; // The created OfferFeedback object
    } catch (error) {
        console.error(`Error submitting feedback for offer ${feedbackData.offerId}:`, error.response?.data || error.message);
        throw error; // Re-throw for component handling
    }
};

/**
 * Retrieves all offer feedback entries.
 * Requires Admin role.
 * @returns {Promise<Array<object>>} An array of OfferFeedback objects.
 */
export const getAllOfferFeedbacks = async () => {
    try {
        const response = await axios.get(`${API_BASE_URL}/api/OfferFeedbacks`);
        return response.data; // Array of OfferFeedback objects
    } catch (error) {
        console.error("Error fetching all offer feedbacks:", error.response?.data || error.message);
        throw error;
    }
};